import React, { useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { IconScan } from './icons/AnalyzerIcons';
import { postScanImage, type ScanImageSuccess } from '../api/endpoints';
import { getErrorMessage } from '../utils/errors';
import type { ImageScannerResult } from './ImageScannerButton';
import FieldSelectorDialog from './FieldSelectorDialog';

type ImageScannerCardProps = {
  onResult: (result: ImageScannerResult, targetField?: 'a' | 'b') => void;
  onError: (error: string) => void;
  disabled?: boolean;
  isPlusOrPremium: boolean;
  className?: string;
  showFieldSelector?: boolean;
  fieldALabel?: string;
  fieldBLabel?: string;
};

const EASE = [0.22, 0.61, 0.36, 1] as [number, number, number, number];

export default function ImageScannerCard({
  onResult,
  onError,
  disabled = false,
  isPlusOrPremium,
  className = '',
  showFieldSelector = false,
  fieldALabel = 'First product',
  fieldBLabel = 'Second product',
}: ImageScannerCardProps) {
  const nav = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [pendingResult, setPendingResult] = useState<ImageScannerResult | null>(null);

  const handleFileSelect = async (file: File) => {
    if (!isPlusOrPremium) {
      onError('Image scanning is available only for Plus and Premium subscribers. Upgrade your plan to unlock this feature!');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      onError('Image too large (max 2MB)');
      return;
    }
    setUploading(true);
    try {
      const result: ScanImageSuccess = await postScanImage(file);
      // barcode is optional: slug-identified products (e.g. local brands
      // without an EAN in open databases) are created without a barcode —
      // product_id is the canonical identifier.
      if (result.ok && result.product_id) {
        const scannerResult: ImageScannerResult = {
          barcode: result.barcode,
          product_id: result.product_id,
          brand: result.brand,
          name: result.name,
          image_url: result.image_url,
        };
        if (showFieldSelector) {
          setPendingResult(scannerResult);
        } else {
          onResult(scannerResult);
        }
      } else if (!result.ok) {
        onError(result.message ?? 'Product not recognized or not a skincare/cosmetic product');
      } else {
        onError('Product not recognized or not a skincare/cosmetic product');
      }
    } catch (error_) {
      onError(getErrorMessage(error_, 'Failed to scan image'));
    } finally {
      setUploading(false);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) void handleFileSelect(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleCameraInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) void handleFileSelect(file);
    if (cameraInputRef.current) cameraInputRef.current.value = '';
  };

  const handleUploadClick = () => {
    if (disabled || uploading) return;
    fileInputRef.current?.click();
  };

  const handleCameraClick = () => {
    if (!isPlusOrPremium) {
      onError('Image scanning is available only for Plus and Premium subscribers. Upgrade your plan to unlock this feature!');
      return;
    }
    if (disabled || uploading) return;
    cameraInputRef.current?.click();
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true);
    else if (e.type === 'dragleave') setDragActive(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files.length > 0) void handleFileSelect(e.dataTransfer.files[0]);
  };

  return (
    <div className={className}>
      <motion.div
        animate={{
          scale: dragActive ? 1.012 : 1,
          boxShadow: dragActive
            ? '0 0 0 2px rgba(42,164,166,1), 0 4px 12px rgba(31,36,33,0.06), 0 24px 48px rgba(31,36,33,0.10)'
            : '0 4px 12px rgba(31,36,33,0.06), 0 24px 48px rgba(31,36,33,0.10)',
        }}
        transition={{ duration: 0.22, ease: EASE }}
        className={`relative overflow-hidden${isPlusOrPremium ? '' : ' scan-pattern'}`}
        style={{
          backgroundColor: isPlusOrPremium ? 'var(--c-cream)' : 'var(--c-sand)',
          borderRadius: 'var(--r-xl)',
          opacity: disabled || uploading ? 0.65 : 1,
          pointerEvents: disabled || uploading ? 'none' : undefined,
        }}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileInputChange} className="hidden" disabled={disabled || uploading} />
        <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" onChange={handleCameraInputChange} className="hidden" disabled={disabled || uploading} />

        {/* Drag teal wash */}
        <AnimatePresence>
          {dragActive && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.16 }}
              className="absolute inset-0 pointer-events-none"
              style={{ background: 'var(--c-teal-weak)', zIndex: 0 }}
            />
          )}
        </AnimatePresence>

        <div className="relative z-[1] p-7 sm:p-8">
          {/* Icon + title */}
          <div className="flex items-start gap-4 mb-5">
            <div
              className="flex-shrink-0 w-12 h-12 rounded-full grid place-items-center"
              style={{
                background: isPlusOrPremium ? 'var(--c-deep-teal)' : 'rgba(244,168,142,0.28)',
                color: isPlusOrPremium ? 'var(--c-cream)' : 'var(--c-error)',
              }}
            >
              <IconScan size={22} />
            </div>

            <div className="flex-1 min-w-0 pt-0.5">
              <div className="flex items-center gap-2.5 mb-1">
                <h3
                  className="font-display text-[20px] leading-tight text-[var(--ink)]"
                  style={{ letterSpacing: '-0.01em' }}
                >
                  Scan product image
                </h3>
                {!isPlusOrPremium && (
                  <span
                    className="text-[9px] font-bold px-2 py-0.5 rounded-full flex-shrink-0"
                    style={{
                      background: 'rgba(244,168,142,0.3)',
                      color: 'var(--c-error)',
                      letterSpacing: '0.1em',
                    }}
                  >
                    PLUS
                  </span>
                )}
              </div>
              <p className="text-sm" style={{ color: 'rgba(31,36,33,0.52)' }}>
                {isPlusOrPremium
                  ? 'Upload or take a photo — AI extracts all details'
                  : 'Upgrade to unlock AI-powered image scanning'}
              </p>
            </div>
          </div>

          {/* Description */}
          <p className="text-[13px] leading-relaxed mb-7" style={{ color: 'rgba(31,36,33,0.52)' }}>
            {isPlusOrPremium
              ? 'Our AI automatically reads the barcode, brand, and product name from your photo. No manual entry needed.'
              : 'Snap a photo of any skincare product and our AI instantly identifies it — barcode, brand, and name extracted in seconds.'}
          </p>

          {/* Action buttons */}
          <div className="flex flex-col sm:flex-row gap-2.5">
            <motion.button
              type="button"
              onClick={handleUploadClick}
              disabled={disabled || uploading}
              whileHover={{ y: -1 }}
              whileTap={{ scale: 0.98 }}
              transition={{ duration: 0.18, ease: EASE }}
              className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 text-sm font-medium rounded-full border transition-colors duration-[180ms] disabled:opacity-50"
              style={isPlusOrPremium ? {
                background: '#FFFFFF',
                color: 'var(--c-charcoal)',
                borderColor: 'var(--c-charcoal-12)',
                boxShadow: '0 1px 4px rgba(31,36,33,0.06)',
              } : {
                background: '#FFFFFF',
                color: 'var(--c-charcoal)',
                borderColor: 'rgba(244,168,142,0.6)',
                borderWidth: '1.5px',
                boxShadow: '0 1px 4px rgba(31,36,33,0.04)',
              }}
            >
              {uploading ? (
                <>
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                    className="w-4 h-4 rounded-full border-2 border-t-transparent flex-shrink-0"
                    style={{ borderColor: 'var(--c-kivo-teal)', borderTopColor: 'transparent' }}
                  />
                  <span>Processing…</span>
                </>
              ) : (
                <>
                  <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24"
                    stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
                    <path d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  <span>Upload image</span>
                </>
              )}
            </motion.button>

            {isPlusOrPremium ? (
              <motion.button
                type="button"
                onClick={handleCameraClick}
                disabled={disabled || uploading}
                whileHover={{ y: -1 }}
                whileTap={{ scale: 0.98 }}
                transition={{ duration: 0.18, ease: EASE }}
                className="btn btn--primary flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 text-sm font-medium rounded-full disabled:opacity-50"
                style={{ boxShadow: '0 2px 8px rgba(26,111,112,0.3)' }}
              >
                <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24"
                  stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
                  <path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z" />
                  <circle cx="12" cy="13" r="4" />
                </svg>
                <span>Take photo</span>
              </motion.button>
            ) : (
              <motion.button
                type="button"
                onClick={() => { nav('/plans'); }}
                whileHover={{ y: -1 }}
                whileTap={{ scale: 0.98 }}
                transition={{ duration: 0.18, ease: EASE }}
                className="btn btn--primary flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 text-sm font-medium rounded-full"
                style={{ boxShadow: '0 2px 8px rgba(26,111,112,0.3)' }}
              >
                <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24"
                  stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
                  <path d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                <span>Upgrade to Plus</span>
              </motion.button>
            )}
          </div>

          {/* Drag hint */}
          {isPlusOrPremium && !uploading && (
            <p
              className="text-[10px] text-center mt-5 font-medium uppercase"
              style={{ color: 'rgba(31,36,33,0.3)', letterSpacing: '0.14em' }}
            >
              or drag & drop an image here
            </p>
          )}
        </div>

        {/* Loading overlay */}
        <AnimatePresence>
          {uploading && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2, ease: EASE }}
              className="absolute inset-0 flex items-center justify-center"
              style={{
                background: 'rgba(248,244,238,0.92)',
                backdropFilter: 'blur(10px)',
                borderRadius: 'var(--r-xl)',
                zIndex: 10,
              }}
            >
              <div className="text-center">
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1.1, repeat: Infinity, ease: 'linear' }}
                  className="w-11 h-11 rounded-full border-[3px] border-t-transparent mx-auto mb-4"
                  style={{ borderColor: 'var(--c-kivo-teal)', borderTopColor: 'transparent' }}
                />
                <p
                  className="font-display text-[18px] text-[var(--ink)]"
                  style={{ letterSpacing: '-0.01em' }}
                >
                  Analysing image…
                </p>
                <p
                  className="text-[12px] mt-1"
                  style={{ color: 'rgba(31,36,33,0.48)' }}
                >
                  Extracting product details
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {showFieldSelector && pendingResult && (
        <FieldSelectorDialog
          product={pendingResult}
          onSelect={(field) => {
            onResult(pendingResult, field);
            setPendingResult(null);
          }}
          onCancel={() => { setPendingResult(null); }}
          fieldALabel={fieldALabel}
          fieldBLabel={fieldBLabel}
        />
      )}
    </div>
  );
}
